import { HistoryRound } from "../commander/Types";
import { transpose, getUTCTime, transformLines } from "./main";

export const trimLine = (
  definition: number[][],
  positions: number,
  dirrection: "left" | "right"
) => {
  if (dirrection !== "left" && dirrection !== "right") return definition;

  const len = definition[0].length;
  const end = dirrection === "left" ? len : len - positions;
  const start = dirrection === "left" ? positions : 0;

  return definition.map((d) => d.slice(start, end));
};

export const transposeIndex = (w: number, h: number, index: number): number => {
  var row = Math.floor(index / w);
  var col = index % w;

  return col * h + row;
};

// gets the definition used in paytable and transforms it
// to positions of the tiles that are part of that line
export const transpileLine = (definition: number[][]) => {
  if (!definition || !definition[0]) return [];

  return transpose(definition)
    .flat()
    .map((n: number, i: number) => (n === 1 ? i : -1))
    .filter((id: number) => id > -1);
};

export const getLineDefinitions = (cfg: any) => {
  if (!cfg || !cfg.paytable) return [];

  const lines = cfg.paytable.sections
    .map((section: any) => section.data)
    .flat()
    .find((d: any) => d.type === "lines");

  return lines?.data ? transformLines(lines.data) : [];
};

export const getLineName = (win: any) => {
  if (typeof win.winline === "undefined") return 1;
  if (win.winline === -1 && win.payout?.symbol) return win.payout.symbol;

  return win.winline + 1;
};

type Int = number;
type Feature = {
  type: string;
  data: { positions: number[]; tileid: number; replacewithid: number };
};

type ReplaceData = { positions: number[]; tile: number };

export const getFeatures = (features: Feature[], type: string) => {
  if (!features) return [];

  return features.filter((feature) => feature.type === type);
};

export const posToAddress = (pos: number, height: number): [number, number] => {
  const x = pos % height;
  const y = Math.floor(pos / height);

  return [x, y];
};

export const addressToPos = (address: [Int, Int], height: Int): Int => {
  const [col, row] = address;

  return col * height + row;
};

export const replaceTiles = (view: number[][], data: ReplaceData[]) => {
  return data.reduce((replacedView: number[][], { positions, tile }) => {
    return replacedView.map((row, i) => {
      return row.map((id, j) =>
        positions.includes(addressToPos([i, j], row.length)) ? tile : id
      );
    });
  }, view);
};

export const getReplaceTilesData = (features?: Feature[]): ReplaceData[] => {
  if (!features || features.length === 0) return [];

  return getFeatures(features, "ReplaceTile").map(({ data }) => {
    return {
      positions: data.positions,
      tile: data.tileid || data.replacewithid,
    };
  });
};

export const parseRound = (data: any, cfg: any) => {
  const lineDefinitions = getLineDefinitions(cfg);
  const spins = data.gameRound?.spins ?? data.gameRound?.round?.spins ?? [];
  const marked = spins.map((s: any) => {
    return (s.wins ?? []).map((win: any) => win.symbol_positions).flat();
  });

  const wins = spins.map((s: any) => {
    return (s.wins ?? []).map((win: any) => {
      const name = getLineName(win);
      const id = `${name}-${Math.random()}`;
      const payout = parseFloat(win.win);

      return {
        id,
        name,
        payout,
        symbol: win.payout?.symbol || 0,
        line: transpileLine(lineDefinitions[name - 1]),
        positions: payout ? win.symbol_positions : [],
        // for some odd reason if the win line index is 0 the "winline" property is missing
        index: win.winline ?? 0,
      };
    });
  });

  return {
    created: data.created,
    roundId: data.roundId,
    sessionId: data.sessionId,
    openningBalance: data.startingBalance / 100,
    closingBalance: data.closingBalance / 100,
    bet: data.bet / 100,
    win: data.win / 100,
    currencyId: data.currencyId,
    views: spins.map((s: any) => s.featureview ?? s.view),
    wins: wins.map((win: any) => win.filter((line: any) => line.index > -1)),
    // An extra field to show some extra information,regarding the wins,
    // like extra details for some bonuses. It is expected this data to be added
    // by the custom history parser in the game.
    extraInfo: null,
    marked: lineDefinitions.length > 0 ? [] : marked,
    payouts: spins.map((s: any) => parseFloat(s.win) ?? 0),
    visible: true,
    ts: getUTCTime(data.created).getTime(),
  };
};

export const parseRounds = (rounds: any): HistoryRound[] => {
  return rounds.map((d: any) => {
    return {
      ...d,
      bet: d.bet / 100,
      win: d.win / 100,
      closingBalance: d.closingBalance / 100,
      ts: getUTCTime(d.created).getTime(),
    };
  });
};
